import { databases } from '../lib/appwrite';
import { ID, Query, Permission, Role } from 'appwrite';

// ============================================
// FOOD IMAGE SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const FOODIMAGE_COLLECTION_ID = process.env.REACT_APP_APPWRITE_FOODIMAGE_COLLECTION_ID;

/**
 * Create a food image record
 * @param {string} foodId - The food item ID
 * @param {string} imageId - The image file ID from Storage
 * @returns {Promise<Object>} - Created food image document
 */
export const createFoodImage = async (foodId, imageId) => {
  try {
    console.log('createFoodImage called with:', { foodId, imageId });
    console.log('Using DATABASE_ID:', DATABASE_ID);
    console.log('Using FOODIMAGE_COLLECTION_ID:', FOODIMAGE_COLLECTION_ID);
    
    if (!DATABASE_ID) {
      throw new Error('DATABASE_ID is not configured in environment variables');
    }
    
    if (!FOODIMAGE_COLLECTION_ID) {
      throw new Error('FOODIMAGE_COLLECTION_ID is not configured in environment variables');
    }
    
    if (!foodId) {
      throw new Error('foodId is required');
    }
    
    if (!imageId) {
      throw new Error('imageId is required');
    }
    
    const foodImage = await databases.createDocument(
      DATABASE_ID,
      FOODIMAGE_COLLECTION_ID,
      ID.unique(),
      {
        food_id: foodId,
        image_id: imageId
      },
      [
        Permission.read(Role.any()),
        Permission.delete(Role.any()) // Allow deletion for cleanup
      ]
    );
    
    console.log('Food image record created successfully:', foodImage);
    return foodImage;
  } catch (error) {
    console.error('Error in createFoodImage:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      type: error.type,
      response: error.response
    });
    throw error;
  }
};

/**
 * Get all images for a food item
 * @param {string} foodId - The food item ID
 * @returns {Promise<Array>} - Array of image IDs
 */
export const getFoodImages = async (foodId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODIMAGE_COLLECTION_ID,
      [Query.equal('food_id', foodId)]
    );
    
    return response.documents.map(doc => ({
      id: doc.$id,
      imageId: doc.image_id,
      foodId: doc.food_id
    }));
  } catch (error) {
    console.error('Error fetching food images:', error);
    throw error;
  }
};

/**
 * Delete a food image record
 * @param {string} documentId - The food image document ID
 */
export const deleteFoodImage = async (documentId) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      FOODIMAGE_COLLECTION_ID,
      documentId
    );
  } catch (error) {
    console.error('Error deleting food image record:', error);
    throw error;
  }
};

/**
 * Delete all images for a food item
 * @param {string} foodId - The food item ID
 */
export const deleteAllFoodImages = async (foodId) => {
  try {
    const images = await getFoodImages(foodId);
    await Promise.all(images.map(img => deleteFoodImage(img.id)));
  } catch (error) {
    console.error('Error deleting all food images:', error);
    throw error;
  }
};

// ============================================
// DATABASE SCHEMA REFERENCE
// ============================================
// 
// FoodImage Collection Schema:
// Collection ID: foodimage
// 
// Attributes:
// - image_id (String): The ID of the uploaded photo in Appwrite Storage
// - food_id (String): Foreign key to FoodItems collection
// 
